import React, { useState, useEffect, useCallback } from 'react';
import API from 'shared/api';
import { fmtMonto } from 'shared/format';
import {
  FiTrendingUp,
  FiBriefcase,
  FiAlertTriangle,
  FiDollarSign,
  FiMapPin,
  FiRefreshCw,
} from 'react-icons/fi';

const COLORS = {
  primary: '#2563eb',
  green: '#059669',
  red: '#dc2626',
  amber: '#d97706',
  purple: '#7c3aed',
  teal: '#0d9488',
  pink: '#db2777',
  indigo: '#4f46e5',
  orange: '#ea580c',
  cyan: '#0891b2',
};

const fechaLocal = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
};

const monthName = (ym) => {
  const months = [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sep',
    'Oct',
    'Nov',
    'Dic',
  ];
  const [y, m] = ym.split('-');
  return months[parseInt(m, 10) - 1] + " '" + y.slice(2);
};

function BarChart({ data, color = COLORS.primary, height = 200 }) {
  if (!data || data.length === 0)
    return (
      <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0' }}>Sin datos</div>
    );
  const maxVal = Math.max(...data.map((d) => d.value), 1);
  const w = Math.max(data.length * 56, 280);
  const pad = { top: 16, right: 8, bottom: 32, left: 32 };
  const chartH = height - pad.top - pad.bottom;
  const barW = Math.min(40, (w - pad.left - pad.right) / data.length - 8);

  return (
    <svg
      viewBox={`0 0 ${w} ${height}`}
      style={{ width: '100%', height, fontFamily: 'Inter, sans-serif' }}
    >
      {[0, 0.25, 0.5, 0.75, 1].map((frac) => {
        const y = pad.top + chartH * (1 - frac);
        const label = Math.round(maxVal * frac);
        return (
          <g key={frac}>
            <line x1={pad.left} y1={y} x2={w - pad.right} y2={y} stroke="#f3f4f6" strokeWidth="1" />
            <text x={pad.left - 6} y={y + 4} textAnchor="end" fill="#9ca3af" fontSize="10">
              {label}
            </text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const x =
          pad.left +
          i * ((w - pad.left - pad.right) / data.length) +
          ((w - pad.left - pad.right) / data.length - barW) / 2;
        const barH = Math.max((d.value / maxVal) * chartH, 2);
        const y = pad.top + chartH - barH;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={barH} rx="4" fill={color} opacity="0.85" />
            <text
              x={x + barW / 2}
              y={pad.top + chartH + 16}
              textAnchor="middle"
              fill="#6b7280"
              fontSize="9"
            >
              {monthName(d.label)}
            </text>
            <text
              x={x + barW / 2}
              y={y - 4}
              textAnchor="middle"
              fill="#374151"
              fontSize="9"
              fontWeight="600"
            >
              {d.value}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function HorizontalBarChart({ data }) {
  if (!data || data.length === 0)
    return (
      <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0' }}>Sin datos</div>
    );
  const maxVal = Math.max(...data.map((d) => d.value), 1);
  const h = data.length * 32 + 12;
  const leftPad = 100;
  const barColors = [
    COLORS.primary,
    COLORS.green,
    COLORS.purple,
    COLORS.amber,
    COLORS.teal,
    COLORS.pink,
    COLORS.indigo,
    COLORS.orange,
  ];

  return (
    <svg
      viewBox={`0 0 400 ${h}`}
      style={{ width: '100%', height: Math.max(h, 100), fontFamily: 'Inter, sans-serif' }}
    >
      {data.map((d, i) => {
        const y = i * 32 + 16;
        const barW = Math.max((d.value / maxVal) * 240, 4);
        return (
          <g key={i}>
            <text x={leftPad - 8} y={y + 4} textAnchor="end" fill="#374151" fontSize="12">
              {d.label}
            </text>
            <rect
              x={leftPad}
              y={y - 6}
              width={barW}
              height="20"
              rx="4"
              fill={barColors[i % barColors.length]}
              opacity="0.85"
            />
            <text x={leftPad + barW + 6} y={y + 4} fill="#6b7280" fontSize="11" fontWeight="600">
              {d.value}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function PieChart({ data }) {
  if (!data || data.length === 0)
    return (
      <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0' }}>Sin datos</div>
    );
  const total = data.reduce((s, d) => s + d.value, 0);
  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  const statusColors = {
    verificado: COLORS.green,
    pendiente: COLORS.amber,
    rechazado: COLORS.red,
    cancelado: '#6b7280',
  };

  let offset = 0;
  const arcs = data.map((d) => {
    const pct = d.value / total;
    const dashLen = pct * circumference;
    const result = { ...d, dashLen, offset };
    offset += dashLen;
    return result;
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
      <svg viewBox="0 0 140 140" style={{ width: '140px', height: '140px', flexShrink: 0 }}>
        {total === 0 ? (
          <circle cx="70" cy="70" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="24" />
        ) : (
          arcs.map((d, i) => (
            <circle
              key={i}
              cx="70"
              cy="70"
              r={radius}
              fill="none"
              stroke={statusColors[d.label] || COLORS.primary}
              strokeWidth="24"
              strokeDasharray={`${d.dashLen} ${circumference - d.dashLen}`}
              strokeDashoffset={-d.offset}
              transform="rotate(-90 70 70)"
              strokeLinecap="butt"
            />
          ))
        )}
        <text x="70" y="68" textAnchor="middle" fill="#111827" fontSize="18" fontWeight="700">
          {total}
        </text>
        <text x="70" y="84" textAnchor="middle" fill="#9ca3af" fontSize="10">
          pagos
        </text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {data.map((d, i) => (
          <div
            key={i}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}
          >
            <div
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '3px',
                background: statusColors[d.label] || COLORS.primary,
                flexShrink: 0,
              }}
            />
            <span style={{ color: '#374151', fontWeight: 500 }}>{d.label}</span>
            <span style={{ color: '#9ca3af', marginLeft: 'auto' }}>{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const C = {
  wrapper: {
    padding: '32px 36px',
    background: '#f8f9fb',
    minHeight: '100vh',
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '32px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  topBarLeft: {},
  title: { fontSize: '22px', fontWeight: 700, color: '#111827', letterSpacing: '-0.4px' },
  subtitle: { fontSize: '13px', color: '#9ca3af', marginTop: '3px' },
  refreshBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '10px',
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#374151',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px',
    marginBottom: '28px',
  },
  card: {
    background: '#fff',
    borderRadius: '16px',
    padding: '22px 24px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 4px 12px rgba(0,0,0,0.02)',
    border: '1px solid #eef0f4',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  cardIcon: (bg) => ({
    width: '48px',
    height: '48px',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: bg,
    flexShrink: 0,
  }),
  cardValue: { fontSize: '26px', fontWeight: 700, color: '#111827', lineHeight: '1.2' },
  cardLabel: { fontSize: '12px', color: '#6b7280', fontWeight: 500, marginTop: '2px' },
  chartRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
    gap: '16px',
    marginBottom: '28px',
  },
  section: {
    background: '#fff',
    borderRadius: '16px',
    padding: '28px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 4px 12px rgba(0,0,0,0.02)',
    border: '1px solid #eef0f4',
  },
  sectionTitle: { fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '18px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left',
    padding: '11px 16px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    borderBottom: '2px solid #f3f4f6',
  },
  td: {
    padding: '12px 16px',
    fontSize: '13px',
    color: '#374151',
    borderBottom: '1px solid #f3f4f6',
  },
  emptyRow: { padding: '48px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' },
  badge: {
    display: 'inline-flex',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
  },
  loading: { padding: '80px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' },
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [historico, setHistorico] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    setError(null);
    let dashOk = false;
    try {
      const rDash = await API.get('/super/dashboard');
      if (rDash.data.status === 'success') {
        setData(rDash.data.data);
        dashOk = true;
      }
    } catch (e) {
      console.error('Error cargando dashboard:', e);
    }
    try {
      const rHist = await API.get('/super/dashboard/historico?meses=12');
      if (rHist.data.status === 'success') setHistorico(rHist.data.data);
    } catch (e) {
      console.error('Error cargando histórico:', e);
    }
    if (!dashOk) setError('No se pudo cargar la información del dashboard. Verifica la conexión.');
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <div style={C.loading}>Cargando...</div>;

  const d = data || {};
  const h = historico || {};

  return (
    <div className="sa-dash-wrapper" style={C.wrapper}>
      <style>{`
        @media (max-width: 768px) {
          .sa-dash-wrapper { padding: 16px 12px !important; }
          .sa-dash-cards { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }
          .sa-dash-card { padding: 16px !important; border-radius: 12px !important; }
          .sa-dash-section { padding: 18px !important; border-radius: 12px !important; }
          .sa-dash-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .sa-dash-table-wrap table { min-width: 500px; }
          .sa-dash-chart-row { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 480px) {
          .sa-dash-cards { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <div style={C.topBar}>
        <div style={C.topBarLeft}>
          <div style={C.title}>Dashboard</div>
          <div style={C.subtitle}>Resumen general de la plataforma</div>
        </div>
        <button
          style={{ ...C.refreshBtn, opacity: refreshing ? 0.5 : 1 }}
          disabled={refreshing}
          onClick={() => load(false)}
        >
          <FiRefreshCw
            size={14}
            style={refreshing ? { animation: 'spin 1s linear infinite' } : undefined}
          />
          {refreshing ? 'Actualizando...' : 'Actualizar'}
        </button>
      </div>

      {error && (
        <div
          style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            padding: '14px 20px',
            marginBottom: '24px',
            color: '#dc2626',
            fontSize: '13px',
            fontWeight: 500,
          }}
        >
          {error}
        </div>
      )}

      <div className="sa-dash-cards" style={C.cards}>
        <div className="sa-dash-card" style={C.card}>
          <div style={C.cardIcon('#eff6ff')}>
            <FiBriefcase size={22} color={COLORS.primary} />
          </div>
          <div>
            <div style={C.cardValue}>{d.total_empresas || 0}</div>
            <div style={C.cardLabel}>Empresas totales</div>
          </div>
        </div>
        <div className="sa-dash-card" style={C.card}>
          <div style={C.cardIcon('#ecfdf5')}>
            <FiTrendingUp size={22} color={COLORS.green} />
          </div>
          <div>
            <div style={C.cardValue}>{d.empresas_activas || 0}</div>
            <div style={C.cardLabel}>Empresas activas</div>
          </div>
        </div>
        <div className="sa-dash-card" style={C.card}>
          <div style={C.cardIcon('#fef2f2')}>
            <FiAlertTriangle size={22} color={COLORS.red} />
          </div>
          <div>
            <div style={C.cardValue}>{d.empresas_vencidas || 0}</div>
            <div style={C.cardLabel}>Empresas vencidas</div>
          </div>
        </div>
        <div className="sa-dash-card" style={C.card}>
          <div style={C.cardIcon('#f0f4ff')}>
            <FiMapPin size={22} color={COLORS.purple} />
          </div>
          <div>
            <div style={C.cardValue}>{d.total_sedes || 0}</div>
            <div style={C.cardLabel}>Sedes totales</div>
          </div>
        </div>
        <div className="sa-dash-card" style={C.card}>
          <div style={C.cardIcon('#fffbeb')}>
            <FiDollarSign size={22} color={COLORS.amber} />
          </div>
          <div>
            <div style={C.cardValue}>${fmtMonto(d.ingreso_estimado)}</div>
            <div style={C.cardLabel}>Ingreso est. mensual</div>
          </div>
        </div>
      </div>

      <div className="sa-dash-chart-row" style={C.chartRow}>
        <div className="sa-dash-section" style={C.section}>
          <div style={C.sectionTitle}>Empresas creadas por mes</div>
          <BarChart
            data={(h.empresas_por_mes || [])
              .slice(-6)
              .map((r) => ({ label: r.mes, value: r.total }))}
            color={COLORS.primary}
            height={200}
          />
        </div>
        <div className="sa-dash-section" style={C.section}>
          <div style={C.sectionTitle}>Ingresos por mes (USD)</div>
          <BarChart
            data={(h.ingresos_por_mes || [])
              .slice(-6)
              .map((r) => ({ label: r.mes, value: r.total_usd }))}
            color={COLORS.green}
            height={200}
          />
        </div>
      </div>

      <div className="sa-dash-section" style={{ ...C.section, marginBottom: '28px' }}>
        <div style={C.sectionTitle}>Proximas a vencer</div>
        <div className="sa-dash-table-wrap">
          <table style={C.table}>
            <thead>
              <tr>
                <th style={C.th}>Empresa</th>
                <th style={C.th}>Vencimiento</th>
                <th style={C.th}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {(d.proximas_vencer || []).length === 0 ? (
                <tr>
                  <td colSpan={3} style={C.emptyRow}>
                    Sin datos
                  </td>
                </tr>
              ) : (
                (d.proximas_vencer || []).map((e, i) => {
                  const vencida =
                    e.fecha_fin && fechaLocal(e.fecha_fin) < new Date(new Date().toDateString());
                  return (
                    <tr key={i}>
                      <td style={{ ...C.td, fontWeight: 600 }}>{e.nombre}</td>
                      <td style={C.td}>
                        {e.fecha_fin ? fechaLocal(e.fecha_fin).toLocaleDateString() : '--'}
                      </td>
                      <td style={C.td}>
                        <span
                          style={{
                            ...C.badge,
                            background: vencida ? '#fef2f2' : '#ecfdf5',
                            color: vencida ? COLORS.red : COLORS.green,
                          }}
                        >
                          {vencida ? 'Vencida' : 'Al dia'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="sa-dash-chart-row" style={C.chartRow}>
        <div className="sa-dash-section" style={C.section}>
          <div style={C.sectionTitle}>Suscripciones por plan</div>
          <HorizontalBarChart
            data={(h.suscripciones_por_plan || []).map((r) => ({ label: r.plan, value: r.total }))}
          />
        </div>
        <div className="sa-dash-section" style={C.section}>
          <div style={C.sectionTitle}>Pagos por estado</div>
          <PieChart
            data={(h.pagos_por_estado || []).map((r) => ({ label: r.estado, value: r.total }))}
          />
        </div>
      </div>
    </div>
  );
}
